import type { Action, Campaign } from "@tgc/types";
import { ActionType } from "@tgc/types";
import { Database, DeviceMobileSpeaker, Heart, Star, Users } from "@phosphor-icons/react";

export type ActionChoice = {
  type: ActionType;
  icon: JSX.Element;
  fields?: ActionField[];
};

/**
 * Décrit les règles de validation déclaratives pour un champ d'action.
 */
export type ActionFieldCheck = {
  maxLength?: number;
  regex?: RegExp;
  startsWith?: string;
};

/**
 * Décrit un champ d'action à compléter par l'utilisateur.
 */
export type ActionField = {
  name: string;
  type: "username" | "url" | "google_place_id" | "message";
  required?: boolean | "one";
  editableOnceSet?: boolean;
  isEditable?: (campaign: Campaign) => boolean;
  check?: ActionFieldCheck;
  legend?: boolean;
  placeholder?: boolean;
};

/**
 * Erreur de validation structurée, traduisible via une clé i18n.
 */
export type ValidationError = {
  /** Code d'erreur pour la traduction (ex: 'validation.maxLength') */
  code: string;
  /** Paramètres additionnels pour le message */
  params?: Record<string, unknown>;
};

/**
 * Valide une valeur selon les règles déclaratives fournies.
 * Retourne une liste d'erreurs structurées (clé de traduction + params).
 * @param value Valeur à valider
 * @param check Règles de validation
 * @returns Tableau d'erreurs (vide si aucune erreur)
 */
export function validateField(
  value: string,
  field?: ActionField,
  check?: ActionFieldCheck
): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!check) return errors;

  if (!value && field?.required === true) return errors;

  if (value && check.maxLength !== undefined && value.length > check.maxLength) {
    errors.push({ code: "validation.maxLength", params: { max: check.maxLength } });
  }
  if (value && check.regex && !new RegExp(check.regex).test(value)) {
    errors.push({ code: "validation.regex", params: { pattern: check.regex } });
  }
  if (value && check.startsWith && !value.startsWith(check.startsWith)) {
    errors.push({ code: "validation.startsWith", params: { prefix: check.startsWith } });
  }

  return errors;
}

export function isActionValid(action: Action): boolean {
  const actionType = action.type as ActionType;
  const actionChoice = actionChoices.find((actionChoice) => actionChoice.type === actionType);
  return (
    actionChoice?.type === ActionType.DATA ? true :
    actionChoice?.fields?.every(
      (field) => validateField(String(action[field.name as keyof Action] ?? ""), field).length === 0
    ) ?? false
  );
}

export function isActionEditable(action: Action, campaign: Campaign) {
  const actionType = action.type as ActionType;
  const actionChoice = actionChoices.find((actionChoice) => actionChoice.type === actionType);
  return actionChoice?.fields?.some((f) => {
    const isFieldEditableOnceSet = f.editableOnceSet ?? true;
    const isFieldSet = action[f.name as keyof Action];
    const isFieldEditableCheck = f.isEditable ? f.isEditable(campaign) : true;

    const isFieldEditable = (isFieldEditableOnceSet || !isFieldSet) && isFieldEditableCheck;
    return isFieldEditable;
  });
}

const actionChoices: ActionChoice[] = [
  {
    type: ActionType.GOOGLE_REVIEW,
    icon: (
      <img src="/assets/actions/google-review.svg" width="18" height="18" alt="Google Review" />
    ),
    fields: [
      {
        name: "target",
        type: "google_place_id",
        required: true,
        editableOnceSet: false,
        isEditable: (campaign: Campaign) => !campaign?.placeId,
      },
    ],
  },
  {
    type: ActionType.INSTAGRAM,
    icon: <img src="/assets/actions/instagram.svg" width="18" height="18" alt="Instagram" />,
    fields: [
      {
        name: "target",
        type: "username",
        required: true,
      },
    ],
  },
  {
    type: ActionType.FACEBOOK,
    icon: <img src="/assets/actions/facebook.svg" width="18" height="18" alt="Facebook" />,
    fields: [
      {
        name: "target",
        type: "username",
        required: true,
      },
    ],
  },
  {
    type: ActionType.TIKTOK,
    icon: <img src="/assets/actions/tiktok.svg" width="18" height="18" alt="TikTok" />,
    fields: [
      {
        name: "target",
        type: "username",
        required: true,
        check: {
          maxLength: 24,
          regex: /^[A-z0-9.-_]{0,23}[A-z0-9_]$/,
        },
      },
    ],
  },
  {
    type: ActionType.TRIPADVISOR,
    icon: <img src="/assets/actions/tripadvisor.svg" width="18" height="18" alt="TripAdvisor" />,
    fields: [
      {
        name: "target",
        type: "url",
        required: true,
        check: {
          startsWith: "https://www.tripadvisor",
        },
      },
    ],
  },
  {
    type: ActionType.DATA,
    icon: <Database weight="bold" size="18px" />,
  },
  {
    type: ActionType.REFERRAL,
    icon: <Users weight="bold" size="18px" />,
    fields: [
      {
        name: "message",
        type: "message",
        required: false,
        legend: true,
        placeholder: true,
      },
    ],
  },
  {
    type: ActionType.CONTENT,
    icon: <Star weight="bold" size="18px" color="#ebab34" />,
    fields: [
      {
        name: "target",
        type: "url",
        check: {
          startsWith: "https://",
        },
      },
    ],
  },
  {
    type: ActionType.LOYALTY_PROGRAM,
    icon: <Heart weight="bold" size="18px" color="#ed1c69" />,
    fields: [
      {
        name: "target",
        type: "url",
        required: true,
        check: {
          startsWith: "https://",
        },
      },
    ],
  },
  {
    type: ActionType.INSTALL_APPLICATION,
    icon: <DeviceMobileSpeaker weight="bold" size="18px" />,
    fields: [
      {
        name: "playStoreUrl",
        type: "url",
        required: "one",
        check: {
          startsWith: "https://play.google.com/store/apps/details?id=",
        },
      },
      {
        name: "appStoreUrl",
        type: "url",
        required: "one",
        check: {
          startsWith: "https://apps.apple.com/",
        },
      },
    ],
  },
];

export default actionChoices;
